import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Tag, Layers, FileText, Megaphone, Mail, BarChart3, X, ArrowLeft, AlertTriangle, Wrench, Sun, Moon, Package, Users, Store, Sparkles, Handshake, Palette } from 'lucide-react';
import { toast } from 'sonner';
import { useState } from 'react';

import { Link, Outlet } from 'react-router-dom';
import { LogOut, Menu } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useDarkMode } from '@/hooks/useDarkMode';
import { ScoutStatusPill } from '@/components/admin/ScoutStatusPill';
export default function AdminLayout({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const darkMode = useDarkMode();

  const handleLogout = () => {
    onLogout();
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/admin/analytics', search: 'manage=products', label: 'Products', icon: Package },
    { path: '/admin/analytics', search: 'manage=merchants', label: 'Merchants', icon: Store },
    { path: '/admin/analytics', search: 'manage=users', label: 'Users', icon: Users },
    { path: '/admin/featured-sections', label: 'Featured Content', icon: Layers },
    { path: '/admin/categories', label: 'Categories', icon: Tag },
    { path: '/admin/archetypes', label: 'Archetype Worlds', icon: Sparkles },
    { path: '/admin/brands', label: 'Brand Directory', icon: Handshake },
    { path: '/admin/ads', label: 'Ad Studio', icon: Palette },
    { path: '/admin/announcements', label: 'Announcements', icon: Megaphone },
    { path: '/admin/email-campaigns', label: 'Email Campaigns', icon: Mail },
    { path: '/admin/client-errors', label: 'Client Errors', icon: AlertTriangle },
    { path: '/admin/terms', label: 'Legal Documents', icon: FileText },
    { path: '/admin/settings', label: 'Site Settings', icon: Wrench }
  ];

  return (
    <div className="min-h-screen flex admin-area transition-colors" style={{ background: darkMode.bgPrimary }}>
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 flex items-center justify-between p-4" style={{ background: '#20212B' }}>
        <div className="flex items-center gap-3">
          <Link
            to="/"
            aria-label="Back to site"
            data-testid="admin-back-to-site-mobile"
            className="p-2 rounded-lg transition-colors hover:bg-[#2D4354]"
            style={{ background: '#2D4354' }}
          >
            <ArrowLeft className="w-5 h-5" style={{ color: '#FED7A5' }} />
          </Link>
          <div>
            <h1 className="text-lg font-bold" style={{ color: '#FED7A5' }}>Mementa Admin</h1>
            <p className="text-xs" style={{ color: '#9CA3AF' }}>{user?.email}</p>
          </div>
        </div>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 rounded-lg"
          style={{ background: '#2D4354' }}
        >
          {mobileMenuOpen ? (
            <X className="w-6 h-6" style={{ color: 'white' }} />
          ) : (
            <Menu className="w-6 h-6" style={{ color: 'white' }} />
          )}
        </button>
      </div>

      {/* Sidebar - Desktop: always visible, Mobile: toggleable */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40
        w-64 flex-shrink-0 flex flex-col
        transition-transform duration-300 ease-in-out
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `} style={{ background: '#20212B', color: 'white' }}>
        {/* Desktop Header */}
        <div className="p-6 border-b hidden lg:block" style={{ borderColor: '#3A3B45' }}>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              aria-label="Back to site"
              title="Back to site"
              data-testid="admin-back-to-site"
              className="p-2 rounded-lg transition-colors hover:bg-[#2D4354] flex-shrink-0"
              style={{ background: '#2D4354' }}
            >
              <ArrowLeft className="w-5 h-5" style={{ color: '#FED7A5' }} />
            </Link>
            <div className="min-w-0">
              <h1 className="text-xl font-bold">Mementa Admin</h1>
              <p className="text-sm mt-0.5 truncate" style={{ color: '#9CA3AF' }}>{user?.email}</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 overflow-y-auto mt-16 lg:mt-0">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.search
              ? location.pathname === item.path && location.search.includes(item.search)
              : location.pathname === item.path && !location.search.includes('manage=');
            return (
              <Link
                key={item.label}
                to={item.search ? `${item.path}?${item.search}` : item.path}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-colors"
                style={{
                  background: isActive ? '#FED7A5' : 'transparent',
                  color: isActive ? '#20212B' : 'white'
                }}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t" style={{ borderColor: '#3A3B45' }}>
          <button
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            className="flex items-center gap-3 px-4 py-3 rounded-lg w-full transition-colors hover:bg-[#2D4354]"
            style={{ color: 'white' }}
            data-testid="admin-theme-toggle"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" style={{ color: '#FED7A5' }} /> : <Moon className="w-5 h-5" />}
            <span className="font-medium">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
          <button
            onClick={() => {
              handleLogout();
              setMobileMenuOpen(false);
            }}
            className="flex items-center gap-3 px-4 py-3 rounded-lg w-full transition-colors hover:bg-red-600"
            style={{ color: 'white' }}
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 pt-16 lg:pt-0 overflow-auto">
        <ScoutStatusPill />
        <Outlet />
      </div>
    </div>
  );
}
